/**
 * TASK STATUS CONSTANTS - Single Source of Truth
 * Used by: Backend, Admin Panel, User Dashboard
 * 
 * These are the ONLY valid values for task.status in the database.
 */

// Canonical task statuses
export const TASK_STATUS = {
  INTAKE: 'intake',
  REVIEW: 'review',
  FILING: 'filing',
  WAITING: 'waiting',
  DONE: 'done',
  CANCELLED: 'cancelled'
};

// Array of all valid statuses (for validation)
export const VALID_STATUSES = Object.values(TASK_STATUS);

// Status pipeline order (for progress calculation)
export const STATUS_PIPELINE = [
  TASK_STATUS.INTAKE,
  TASK_STATUS.REVIEW,
  TASK_STATUS.FILING,
  TASK_STATUS.WAITING,
  TASK_STATUS.DONE
];

// Status display configuration
export const STATUS_DISPLAY = {
  [TASK_STATUS.INTAKE]: {
    label: 'Intake',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    description: 'Gathering information'
  },
  [TASK_STATUS.REVIEW]: {
    label: 'Review',
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    description: 'Expert review in progress'
  },
  [TASK_STATUS.FILING]: {
    label: 'Filing',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    description: 'Submitting to authorities'
  },
  [TASK_STATUS.WAITING]: {
    label: 'Waiting',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    description: 'Awaiting authority response'
  },
  [TASK_STATUS.DONE]: {
    label: 'Done',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    description: 'Successfully completed'
  },
  [TASK_STATUS.CANCELLED]: {
    label: 'Cancelled',
    color: 'bg-slate-100 text-slate-600 border-slate-200',
    description: 'Task was cancelled'
  }
};

// Legacy status mapping (for migration/normalization)
export const LEGACY_STATUS_MAP = {
  'pending': TASK_STATUS.INTAKE,
  'paid': TASK_STATUS.INTAKE,
  'processing': TASK_STATUS.REVIEW,
  'in_review': TASK_STATUS.REVIEW,
  'submitted': TASK_STATUS.FILING,
  'awaiting': TASK_STATUS.WAITING,
  'completed': TASK_STATUS.DONE,
  'cancelled': TASK_STATUS.CANCELLED
};

/**
 * Normalize a status value to canonical format
 * Handles legacy values and ensures consistency
 */
export function normalizeStatus(status) {
  if (!status) return TASK_STATUS.INTAKE;
  
  const lowerStatus = status.toLowerCase();
  
  // If already canonical, return as-is
  if (VALID_STATUSES.includes(lowerStatus)) {
    return lowerStatus;
  }
  
  // Map legacy status to canonical
  if (LEGACY_STATUS_MAP[lowerStatus]) {
    return LEGACY_STATUS_MAP[lowerStatus];
  }
  
  // Default to intake for unknown statuses
  return TASK_STATUS.INTAKE;
}

/**
 * Get progress percentage based on status
 */
export function getProgressFromStatus(status) {
  const progressMap = {
    [TASK_STATUS.INTAKE]: 10,
    [TASK_STATUS.REVIEW]: 40,
    [TASK_STATUS.FILING]: 60,
    [TASK_STATUS.WAITING]: 80,
    [TASK_STATUS.DONE]: 100,
    [TASK_STATUS.CANCELLED]: 0
  };
  return progressMap[normalizeStatus(status)] || 0;
}

/**
 * Check if status is "in progress" (active, not done/cancelled)
 */
export function isInProgress(status) {
  const normalized = normalizeStatus(status);
  return [TASK_STATUS.REVIEW, TASK_STATUS.FILING, TASK_STATUS.WAITING].includes(normalized);
}

/**
 * Check if status is active (not done or cancelled)
 */
export function isActive(status) {
  const normalized = normalizeStatus(status);
  return normalized !== TASK_STATUS.DONE && normalized !== TASK_STATUS.CANCELLED;
}

// Admin panel counter groupings
export const STATUS_GROUPS = {
  PENDING_INTAKE: [TASK_STATUS.INTAKE],
  IN_PROGRESS: [TASK_STATUS.REVIEW, TASK_STATUS.FILING, TASK_STATUS.WAITING],
  DONE: [TASK_STATUS.DONE],
  CANCELLED: [TASK_STATUS.CANCELLED]
};
